import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import {
  createVehicleSchema,
  type CreateVehicleInput,
  type UpdateVehicleInput,
  type Vehicle,
} from '@fdt/shared';
import { Chip, MenuItem, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CrudPage } from '../components/CrudPage';
import type { Column } from '../components/DataTable';
import { StatusChip } from '../components/StatusChip';
import { vehiclesApi } from '../resources';

interface FormState {
  plate: string;
  label: string;
  notes: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = { plate: '', label: '', notes: '', isActive: true };

export function VehiclesPage() {
  const { t } = useTranslation();

  const columns: Column<Vehicle>[] = [
    { id: 'plate', label: t('fields.plate'), render: (row) => row.plate, sortable: true },
    { id: 'label', label: t('fields.label'), render: (row) => row.label ?? '—', sortable: true },
    {
      id: 'assigned',
      label: t('vehicles.assignedStaff'),
      render: (row) =>
        row.assignedUserIds.length === 0 ? (
          <Chip size="small" variant="outlined" label={t('vehicles.unassigned')} />
        ) : (
          <Chip
            size="small"
            color="primary"
            label={t('vehicles.assignedCount', { count: row.assignedUserIds.length })}
          />
        ),
    },
    { id: 'notes', label: t('fields.notes'), render: (row) => row.notes ?? '—' },
    {
      id: 'isActive',
      label: t('fields.status'),
      render: (row) => <StatusChip active={row.isActive} />,
    },
  ];

  return (
    <CrudPage<Vehicle, FormState, CreateVehicleInput, UpdateVehicleInput>
      title={t('nav.vehicles')}
      icon={LocalShippingIcon}
      tone="accent"
      resourceKey="vehicles"
      api={vehiclesApi}
      columns={columns}
      rowKey={(row) => row.id}
      emptyForm={EMPTY_FORM}
      defaultSortBy="plate"
      schema={createVehicleSchema}
      toForm={(row) => ({
        plate: row.plate,
        label: row.label ?? '',
        notes: row.notes ?? '',
        isActive: row.isActive,
      })}
      toPayload={(form) => ({
        plate: form.plate,
        label: form.label || null,
        notes: form.notes || null,
        isActive: form.isActive,
      })}
      createTitle={t('vehicles.createTitle')}
      editTitle={t('vehicles.editTitle')}
      deleteTitle={t('vehicles.deleteTitle')}
      deleteMessage={(row) => t('vehicles.deleteMessage', { plate: row.plate })}
      renderForm={({ form, setForm, fieldErrors }) => (
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('fields.plate')}
              value={form.plate}
              onChange={(event) => setForm({ ...form, plate: event.target.value })}
              error={Boolean(fieldErrors.plate)}
              helperText={fieldErrors.plate ? t(fieldErrors.plate) : t('vehicles.plateHelp')}
              required
              fullWidth
            />
            <TextField
              label={t('fields.label')}
              value={form.label}
              onChange={(event) => setForm({ ...form, label: event.target.value })}
              fullWidth
            />
            <TextField
              select
              label={t('fields.status')}
              value={form.isActive ? 'true' : 'false'}
              onChange={(event) => setForm({ ...form, isActive: event.target.value === 'true' })}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="true">{t('common.active')}</MenuItem>
              <MenuItem value="false">{t('common.inactive')}</MenuItem>
            </TextField>
          </Stack>

          <TextField
            label={t('fields.notes')}
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      )}
    />
  );
}
